import { useEffect, useMemo, useRef, useState } from "react";
import { UNIQUE_GOOGLE_FONTS, DEFAULT_HEADLINE_TYPOGRAPHY } from "../data/googleFonts";
import { loadFontPreview, loadGoogleFont } from "../lib/googleFontLoader";
import {
  fetchTypography,
  saveTypography,
  resetTypography,
  fetchTypographyPresets,
  createTypographyPreset,
  deleteTypographyPreset,
} from "../lib/settingsApi";
import { projects } from "../data/projects";

// Discrete slider/dropdown ranges — per spec.
// Sizes 24-96 in 4px steps → 19 values. Weights 100-900 in 100 steps → 9 values.
const SIZE_STEPS = Array.from({ length: (96 - 24) / 4 + 1 }, (_, i) => 24 + i * 4);
const WEIGHT_STEPS = [100, 200, 300, 400, 500, 600, 700, 800, 900];
// Line-height: 0.9-2.0 step 0.05 → 23 values. Rounded to 2 decimals to
// avoid floating-point drift in the <select value>.
const LINE_HEIGHT_STEPS = Array.from({ length: 23 }, (_, i) =>
  Number((0.9 + i * 0.05).toFixed(2))
);
// Letter-spacing: -0.05em to 0.20em step 0.01 → 26 values.
const LETTER_SPACING_STEPS = Array.from({ length: 26 }, (_, i) =>
  Number((-0.05 + i * 0.01).toFixed(2))
);

const SAMPLE_TEXT = "The Big Long Story — Case Film";

// One row of the font dropdown. IntersectionObserver-driven font load so
// scrolling through 150 options doesn't fire 150 network requests up-front.
function FontOption({ font, active, onSelect }) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    if (!ref.current || loaded) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            loadFontPreview(font.family);
            setLoaded(true);
            io.disconnect();
          }
        });
      },
      { root: el.closest("[data-font-list]"), rootMargin: "40px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [font.family, loaded]);
  return (
    <button
      ref={ref}
      type="button"
      onClick={() => onSelect(font.family)}
      data-testid={`typography-font-option-${font.family.replace(/\s+/g, "-").toLowerCase()}`}
      className={`w-full text-left px-4 py-2.5 hover:bg-ink/5 flex items-baseline justify-between gap-3 transition-colors ${
        active ? "bg-ink/8" : ""
      }`}
    >
      <span
        style={{ fontFamily: `"${font.family}", ${font.category}` }}
        className="text-[16px] truncate"
      >
        {font.family}
      </span>
      <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-ink/40 shrink-0">
        {font.category}
      </span>
    </button>
  );
}

export default function TypographyPanel({ token, onSaveMessage }) {
  const [loaded, setLoaded] = useState(false);
  const [settings, setSettings] = useState(DEFAULT_HEADLINE_TYPOGRAPHY);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  // ---- Presets state ----
  // `presets` is the server-side list; `presetName` + `presetInputOpen`
  // gate the tiny "Save as preset..." input row. Deletion runs in-place
  // with a lightweight `deletingId` marker so the button can show a
  // spinner without blocking the rest of the list.
  const [presets, setPresets] = useState([]);
  const [presetsLoaded, setPresetsLoaded] = useState(false);
  const [presetInputOpen, setPresetInputOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetSaving, setPresetSaving] = useState(false);
  const [deletingPresetId, setDeletingPresetId] = useState(null);
  const presetInputRef = useRef(null);

  // Fetch existing settings on mount so the form reflects what's live.
  useEffect(() => {
    let cancelled = false;
    fetchTypography()
      .then((data) => {
        if (cancelled) return;
        setSettings({ ...DEFAULT_HEADLINE_TYPOGRAPHY, ...data });
        setLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch presets in parallel with the main settings — cheap admin-only
  // endpoint and keeps the section responsive when the panel opens.
  useEffect(() => {
    let cancelled = false;
    fetchTypographyPresets(token)
      .then((list) => {
        if (cancelled) return;
        setPresets(list || []);
        setPresetsLoaded(true);
        // Warm each preset's Google Font so its name renders in its own
        // typeface in the preset chip (~1 request per unique family,
        // browser-cached across pages).
        (list || []).forEach((p) => p.font_family && loadFontPreview(p.font_family));
      })
      .catch(() => {
        if (!cancelled) setPresetsLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Auto-focus the preset name input when the row expands.
  useEffect(() => {
    if (presetInputOpen) {
      const t = setTimeout(() => presetInputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [presetInputOpen]);

  // Load the CURRENTLY-selected font at the CURRENTLY-selected weight so the
  // live preview reflects the exact combination. Family lookup lets us pick
  // the nearest published weight if the exact one isn't available (Google's
  // API 400s on unpublished weights, breaking the preview).
  useEffect(() => {
    const font = UNIQUE_GOOGLE_FONTS.find(
      (f) => f.family === settings.font_family
    );
    if (!font) return;
    // Nearest available weight in the family's published set.
    const nearest = font.weights.reduce(
      (best, w) =>
        Math.abs(w - settings.font_weight) < Math.abs(best - settings.font_weight)
          ? w
          : best,
      font.weights[0]
    );
    loadGoogleFont(font.family, [nearest]);
  }, [settings.font_family, settings.font_weight]);

  // ---- Font search dropdown state ----
  const [fontSearchOpen, setFontSearchOpen] = useState(false);
  const [fontQuery, setFontQuery] = useState("");
  const searchInputRef = useRef(null);
  const filteredFonts = useMemo(() => {
    const q = fontQuery.trim().toLowerCase();
    if (!q) return UNIQUE_GOOGLE_FONTS;
    return UNIQUE_GOOGLE_FONTS.filter(
      (f) =>
        f.family.toLowerCase().includes(q) ||
        f.category.toLowerCase().includes(q)
    );
  }, [fontQuery]);

  const dropdownRef = useRef(null);
  useEffect(() => {
    if (!fontSearchOpen) return;
    // Focus search input on open, and close on outside click.
    const timer = setTimeout(() => searchInputRef.current?.focus(), 20);
    const onDocClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setFontSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", onDocClick);
    };
  }, [fontSearchOpen]);

  const update = (partial) => setSettings((prev) => ({ ...prev, ...partial }));

  const handleSelectFont = (family) => {
    // When switching family, snap the weight to the nearest published one
    // so the preview never lands on a missing weight.
    const font = UNIQUE_GOOGLE_FONTS.find((f) => f.family === family);
    let nextWeight = settings.font_weight;
    if (font) {
      nextWeight = font.weights.reduce(
        (best, w) =>
          Math.abs(w - settings.font_weight) < Math.abs(best - settings.font_weight)
            ? w
            : best,
        font.weights[0]
      );
    }
    update({ font_family: family, font_weight: nextWeight });
    setFontSearchOpen(false);
    setFontQuery("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveTypography(settings, token);
      onSaveMessage?.("Saved");
    } catch (err) {
      onSaveMessage?.(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      const data = await resetTypography(token);
      // Server returns the defaults in the response body — hydrate the form
      // with them so the preview updates immediately.
      const next = { ...DEFAULT_HEADLINE_TYPOGRAPHY };
      ["font_family", "font_size", "font_weight", "line_height", "letter_spacing"].forEach((k) => {
        if (data && data[k] !== undefined) next[k] = data[k];
      });
      setSettings(next);
      onSaveMessage?.("Reset to defaults");
    } catch (err) {
      onSaveMessage?.(err.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  // ---- Preset actions ----

  const handleApplyPreset = (preset) => {
    // Apply-only: seed the form with the preset's values, but DON'T write
    // to typography_settings. The admin still has to click Save to persist
    // — keeps preset browsing side-effect free.
    // Spread DEFAULT_HEADLINE_TYPOGRAPHY first so any future 6th field
    // that legacy presets don't carry falls back to a sensible default
    // instead of becoming undefined and blowing up the preview CSS.
    setSettings({
      ...DEFAULT_HEADLINE_TYPOGRAPHY,
      font_family: preset.font_family,
      font_size: preset.font_size,
      font_weight: preset.font_weight,
      line_height: preset.line_height,
      letter_spacing: preset.letter_spacing,
    });
    onSaveMessage?.(`Applied "${preset.name}" (unsaved)`);
  };

  const handleCreatePreset = async () => {
    const name = presetName.trim();
    if (!name) return;
    setPresetSaving(true);
    try {
      const created = await createTypographyPreset({ name, ...settings }, token);
      setPresets((prev) => [created, ...prev]);
      setPresetName("");
      setPresetInputOpen(false);
      onSaveMessage?.(`Saved preset "${created.name}"`);
    } catch (err) {
      onSaveMessage?.(err.response?.data?.detail || "Preset save failed");
    } finally {
      setPresetSaving(false);
    }
  };

  const handleDeletePreset = async (preset) => {
    // Confirm before wiping — presets aren't recoverable and it's a
    // single-click action that could hit by accident.
    if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
    setDeletingPresetId(preset.id);
    try {
      await deleteTypographyPreset(preset.id, token);
      setPresets((prev) => prev.filter((p) => p.id !== preset.id));
      onSaveMessage?.(`Deleted preset "${preset.name}"`);
    } catch (err) {
      onSaveMessage?.(err.response?.data?.detail || "Delete failed");
    } finally {
      setDeletingPresetId(null);
    }
  };

  // Available weights for the currently-selected font — restricts the weight
  // dropdown so the admin can't pick an unpublished weight that renders as
  // fallback in the preview.
  const currentFont = UNIQUE_GOOGLE_FONTS.find(
    (f) => f.family === settings.font_family
  );
  const availableWeights = currentFont?.weights || WEIGHT_STEPS;

  // Hero-preview asset — the still-frame image for a real project so the
  // admin can see how the current typography looks over an actual portfolio
  // hero. First project with a transitionImage wins; empty string falls
  // back to a solid ink background inside the preview frame.
  const heroSourceProject = useMemo(
    () => projects.find((p) => p.transitionImage) || projects[0] || null,
    []
  );
  const heroImage = heroSourceProject?.transitionImage || "";
  const heroTitle = heroSourceProject?.title || "The Big Long Story";

  if (!loaded) {
    return (
      <div data-testid="typography-panel-loading" className="font-sans text-[13px] text-ink/50">
        Loading typography…
      </div>
    );
  }

  return (
    <div data-testid="typography-panel" className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-8 lg:gap-12">
      {/* ---- Form column ---- */}
      <div className="space-y-6">
        {/* Font family — searchable dropdown */}
        <div>
          <label
            htmlFor="typography-font-trigger"
            className="block font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2"
          >
            Headline font
          </label>
          <div ref={dropdownRef} className="relative">
            <button
              id="typography-font-trigger"
              type="button"
              onClick={() => setFontSearchOpen((v) => !v)}
              data-testid="typography-font-trigger"
              className="w-full flex items-center justify-between gap-3 px-4 py-3 border border-ink/20 hover:border-ink/40 focus:border-ink transition-colors bg-paper text-left"
            >
              <span
                style={{ fontFamily: `"${settings.font_family}", serif` }}
                className="text-[17px] truncate"
              >
                {settings.font_family}
              </span>
              <span className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/40 shrink-0">
                {fontSearchOpen ? "Close" : "Change"}
              </span>
            </button>

            {fontSearchOpen && (
              <div
                data-testid="typography-font-list"
                data-font-list
                className="absolute z-30 mt-2 left-0 right-0 bg-paper border border-ink/15 shadow-xl max-h-[360px] flex flex-col"
              >
                <div className="p-3 border-b border-ink/10">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={fontQuery}
                    onChange={(e) => setFontQuery(e.target.value)}
                    placeholder="Search fonts…"
                    data-testid="typography-font-search"
                    className="w-full px-3 py-2 font-sans text-[13px] border border-ink/15 focus:border-ink outline-none bg-paper"
                  />
                </div>
                <div className="overflow-y-auto flex-1">
                  {filteredFonts.length === 0 && (
                    <div className="font-sans text-[13px] text-ink/50 px-4 py-6">
                      No fonts match &ldquo;{fontQuery}&rdquo;.
                    </div>
                  )}
                  {filteredFonts.map((f) => (
                    <FontOption
                      key={f.family}
                      font={f}
                      active={f.family === settings.font_family}
                      onSelect={handleSelectFont}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Size + weight — side by side on desktop */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="typography-font-size"
              className="block font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2"
            >
              Font size
            </label>
            <select
              id="typography-font-size"
              value={settings.font_size}
              onChange={(e) => update({ font_size: Number(e.target.value) })}
              data-testid="typography-font-size"
              className="w-full px-4 py-3 border border-ink/20 hover:border-ink/40 focus:border-ink transition-colors bg-paper font-sans text-[14px]"
            >
              {SIZE_STEPS.map((s) => (
                <option key={s} value={s}>
                  {s}px
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="typography-font-weight"
              className="block font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2"
            >
              Font weight
            </label>
            <select
              id="typography-font-weight"
              value={settings.font_weight}
              onChange={(e) => update({ font_weight: Number(e.target.value) })}
              data-testid="typography-font-weight"
              className="w-full px-4 py-3 border border-ink/20 hover:border-ink/40 focus:border-ink transition-colors bg-paper font-sans text-[14px]"
            >
              {WEIGHT_STEPS.map((w) => {
                const available = availableWeights.includes(w);
                return (
                  <option key={w} value={w} disabled={!available}>
                    {w}
                    {available ? "" : " — unavailable in this font"}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        {/* Line height + letter spacing */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="typography-line-height"
              className="block font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2"
            >
              Line height
            </label>
            <select
              id="typography-line-height"
              value={settings.line_height}
              onChange={(e) => update({ line_height: Number(e.target.value) })}
              data-testid="typography-line-height"
              className="w-full px-4 py-3 border border-ink/20 hover:border-ink/40 focus:border-ink transition-colors bg-paper font-sans text-[14px]"
            >
              {LINE_HEIGHT_STEPS.map((v) => (
                <option key={v} value={v}>
                  {v.toFixed(2)}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label
              htmlFor="typography-letter-spacing"
              className="block font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2"
            >
              Letter spacing
            </label>
            <select
              id="typography-letter-spacing"
              value={settings.letter_spacing}
              onChange={(e) => update({ letter_spacing: Number(e.target.value) })}
              data-testid="typography-letter-spacing"
              className="w-full px-4 py-3 border border-ink/20 hover:border-ink/40 focus:border-ink transition-colors bg-paper font-sans text-[14px]"
            >
              {LETTER_SPACING_STEPS.map((v) => (
                <option key={v} value={v}>
                  {v.toFixed(2)}em
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Save + Reset row */}
        <div className="flex items-center gap-3 pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            data-testid="typography-save"
            className="bg-ink text-paper px-5 py-2.5 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
          >
            {saving ? "Saving…" : "Save typography"}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={resetting}
            data-testid="typography-reset"
            className="px-4 py-2.5 font-sans text-[10px] tracking-[0.32em] uppercase text-ink/60 hover:text-ink border border-ink/20 hover:border-ink/60 disabled:opacity-40 transition-colors"
          >
            {resetting ? "Resetting…" : "Reset to default"}
          </button>
        </div>

        {/* ---- Presets section ---- */}
        <div
          data-testid="typography-presets-section"
          className="pt-6 mt-2 border-t border-ink/10"
        >
          <div className="flex items-center justify-between mb-3">
            <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50">
              Saved presets
            </div>
            {!presetInputOpen && (
              <button
                type="button"
                onClick={() => setPresetInputOpen(true)}
                data-testid="typography-preset-add"
                className="font-sans text-[10px] tracking-[0.28em] uppercase text-ink/60 hover:text-ink transition-colors"
              >
                + Save current as preset
              </button>
            )}
          </div>

          {presetInputOpen && (
            <div
              data-testid="typography-preset-input-row"
              className="flex items-center gap-2 mb-3"
            >
              <input
                ref={presetInputRef}
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleCreatePreset();
                  } else if (e.key === "Escape") {
                    setPresetInputOpen(false);
                    setPresetName("");
                  }
                }}
                placeholder="Preset name (e.g. Editorial Serif)"
                maxLength={60}
                data-testid="typography-preset-name-input"
                className="flex-1 px-3 py-2 font-sans text-[13px] border border-ink/20 focus:border-ink outline-none bg-paper"
              />
              <button
                type="button"
                onClick={handleCreatePreset}
                disabled={presetSaving || !presetName.trim()}
                data-testid="typography-preset-save"
                className="bg-ink text-paper px-4 py-2 font-sans text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 hover:opacity-90 transition-opacity"
              >
                {presetSaving ? "Saving…" : "Save"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setPresetInputOpen(false);
                  setPresetName("");
                }}
                data-testid="typography-preset-cancel"
                className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 hover:text-ink px-2 py-2 transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {!presetsLoaded ? (
            <div className="font-sans text-[12px] text-ink/40">Loading presets…</div>
          ) : presets.length === 0 ? (
            <div
              data-testid="typography-presets-empty"
              className="font-sans text-[12px] text-ink/40 leading-relaxed"
            >
              No presets saved yet. Tune the form above and click{" "}
              <em className="not-italic text-ink/70">+ Save current as preset</em>{" "}
              to bookmark this combo.
            </div>
          ) : (
            <ul
              data-testid="typography-presets-list"
              className="space-y-2"
            >
              {presets.map((p) => {
                // Testid uses BOTH the kebab-name (for human readability
                // in test scripts) AND the preset id suffix (for
                // uniqueness — the backend intentionally allows duplicate
                // names, so kebab-name alone can collide).
                const kebab = p.name.replace(/\s+/g, "-").toLowerCase();
                const testKey = `${kebab}-${p.id.slice(0, 8)}`;
                return (
                  <li
                    key={p.id}
                    data-testid={`typography-preset-${p.id}`}
                    className="group flex items-center gap-3 border border-ink/10 hover:border-ink/30 px-3 py-2 transition-colors"
                  >
                    <button
                      type="button"
                      onClick={() => handleApplyPreset(p)}
                      data-testid={`typography-preset-apply-${testKey}`}
                      className="flex-1 min-w-0 text-left flex items-baseline gap-3"
                    >
                      <span
                        style={{
                          fontFamily: `"${p.font_family}", serif`,
                          fontWeight: p.font_weight,
                        }}
                        className="text-[15px] truncate"
                      >
                        {p.name}
                      </span>
                      <span className="font-sans text-[10px] tracking-[0.24em] uppercase text-ink/40 shrink-0 truncate">
                        {p.font_family} · {p.font_size}px · {p.font_weight}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDeletePreset(p)}
                      disabled={deletingPresetId === p.id}
                      data-testid={`typography-preset-delete-${testKey}`}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 font-sans text-[10px] tracking-[0.28em] uppercase text-red-600/70 hover:text-red-600 disabled:opacity-40 transition-opacity"
                      aria-label={`Delete preset ${p.name}`}
                    >
                      {deletingPresetId === p.id ? "…" : "Delete"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <p className="font-sans text-[12px] leading-relaxed text-ink/50 pt-4 max-w-md">
          Typography applies only to the big project title overlaid on each
          portfolio scroll section. Header, admin UI, and body copy elsewhere on
          the site are untouched. Changes are live for visitors the moment you
          save — the homepage picks them up on next load.
        </p>
      </div>

      {/* ---- Live preview column ---- */}
      <div className="space-y-6">
        {/* Sample text preview */}
        <div>
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2">
            Sample text
          </div>
          <div
            data-testid="typography-preview-frame"
            className="relative bg-ink text-paper rounded-[2px] overflow-hidden aspect-[16/9] flex items-center px-8 md:px-12"
          >
            {/* Subtle diagonal grain to hint at the on-page environment */}
            <div
              className="absolute inset-0 opacity-[0.06] pointer-events-none"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(45deg,transparent 0 4px,#ffffff 4px 5px)",
              }}
            />
            <h2
              data-testid="typography-preview-headline"
              style={{
                fontFamily: `"${settings.font_family}", ${currentFont?.category || "serif"}`,
                fontSize: `${settings.font_size}px`,
                fontWeight: settings.font_weight,
                lineHeight: settings.line_height,
                letterSpacing: `${settings.letter_spacing}em`,
                // Prevent the preview from spilling out of the frame on very
                // large sizes — the admin can still see the type, but caps
                // the browser layout at the container width.
                maxWidth: "100%",
                overflowWrap: "break-word",
              }}
              className="relative z-10"
            >
              {SAMPLE_TEXT}
            </h2>
          </div>
        </div>

        {/* Real portfolio hero mini-preview — shows the headline OVER an
            actual project's hero image so the admin can gauge contrast,
            weight, and size in-situ before saving. Uses the first static
            project's transitionImage; falls back to a solid ink background
            if the asset isn't reachable. */}
        <div>
          <div className="font-sans text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-2">
            On a portfolio hero
          </div>
          <div
            data-testid="typography-hero-preview"
            className="relative bg-black text-paper overflow-hidden aspect-[16/9] rounded-[2px]"
          >
            {heroImage && (
              <img
                src={heroImage}
                alt=""
                data-testid="typography-hero-preview-image"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            )}
            {/* Same bottom-up scrim used on the real portfolio for legibility */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent pointer-events-none" />
            {/* Grain layer, same as PortfolioSection */}
            <div className="grain absolute inset-0 pointer-events-none" />
            <div className="absolute inset-0 flex items-end justify-start p-6 md:p-8">
              <h2
                data-testid="typography-hero-preview-headline"
                style={{
                  fontFamily: `"${settings.font_family}", ${currentFont?.category || "serif"}`,
                  // Mini-preview uses ~55% of the saved size so a 96px
                  // headline still fits inside the compact preview frame
                  // AND accurately reflects the mobile-scaled behaviour.
                  fontSize: `${Math.max(14, settings.font_size * 0.55)}px`,
                  fontWeight: settings.font_weight,
                  lineHeight: settings.line_height,
                  letterSpacing: `${settings.letter_spacing}em`,
                  maxWidth: "min(30ch, 100%)",
                  overflowWrap: "break-word",
                }}
                className="text-white drop-shadow-sm"
              >
                {heroTitle}
              </h2>
            </div>
          </div>
        </div>

        <dl
          data-testid="typography-preview-values"
          className="grid grid-cols-2 md:grid-cols-5 gap-3 text-[11px] font-sans"
        >
          {[
            ["Font", settings.font_family],
            ["Size", `${settings.font_size}px`],
            ["Weight", settings.font_weight],
            ["Line", settings.line_height.toFixed(2)],
            ["Track", `${settings.letter_spacing.toFixed(2)}em`],
          ].map(([label, value]) => (
            <div key={label} className="border-t border-ink/10 pt-2">
              <div className="text-[9px] tracking-[0.28em] uppercase text-ink/40">
                {label}
              </div>
              <div className="text-ink/80 truncate mt-0.5">{value}</div>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
